/**
 * Shared wrapper for documentation pages.
 *
 * Renders a title with the copy button group on the right,
 * then markdown content, then previous/next navigation links.
 */

import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownComponents } from "@/lib/markdown/components";
import { groupCodeBlocks } from "@/lib/markdown/group-code-blocks";
import { DocsCopyButton } from "./docs-copy-button";
import { ALL_NAV_ITEMS } from "./docs-sidebar";

interface DocsPageProps {
	/** Page title displayed as h1 */
	title: string;
	/** Raw markdown content string */
	content: string;
}

export function DocsPage({ title, content }: DocsPageProps) {
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;

	const currentIndex = ALL_NAV_ITEMS.findIndex((item) => item.href === currentPath);
	const prev = currentIndex > 0 ? ALL_NAV_ITEMS[currentIndex - 1] : null;
	const next = currentIndex < ALL_NAV_ITEMS.length - 1 ? ALL_NAV_ITEMS[currentIndex + 1] : null;
	const processed = useMemo(() => groupCodeBlocks(content), [content]);

	return (
		<div className="max-w-3xl">
			<div className="mb-8 flex items-center justify-between">
				<h1 className="text-3xl font-bold tracking-tight">{title}</h1>
				<DocsCopyButton markdown={content} />
			</div>
			<div className="prose prose-sm max-w-none dark:prose-invert">
				<ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
					{processed}
				</ReactMarkdown>
			</div>

			{/* Prev / Next navigation */}
			{(prev || next) && (
				<nav className="mt-12 flex items-center justify-between border-t pt-6">
					{prev ? (
						<Link
							to={prev.href}
							className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							<ChevronLeft className="h-4 w-4" />
							{prev.label}
						</Link>
					) : (
						<span />
					)}
					{next ? (
						<Link
							to={next.href}
							className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							{next.label}
							<ChevronRight className="h-4 w-4" />
						</Link>
					) : (
						<span />
					)}
				</nav>
			)}
		</div>
	);
}
